// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Centreline paths for ramps and wire habitrails.
//
// A Catmull-Rom spline through the author's control points, **sampled once at load** into a
// dense polyline with cumulative arc length. Nothing evaluates a cubic at runtime.
//
// That choice is deliberate on three counts. It removes every transcendental from the hot
// path — Catmull-Rom is pure polynomial arithmetic, and after sampling even that is gone,
// leaving `+ − × ÷ √` and comparisons (PH-081). It makes arc length exact by construction
// rather than by numeric integration, so a ball's progress along a ramp cannot drift. And
// it lets a query resume from where the same ball was last step, which turns a scan of a
// hundred and sixty samples into a scan of nine.
//
// Parameterisation is by **three-dimensional** arc length. A ramp needs the height gradient
// per unit of *planar* travel and a habitrail needs true distance along the wire; both fall
// out of the 3-D tangent, whereas a planar parameterisation would quietly understate a
// steep climb.

/** A resolved query against a path. Reused across calls; the hot path allocates nothing. */
export interface PathQuery {
  /** Arc length of the closest point, metres from the start. */
  s: number
  /** Sample index of the segment it landed on, usable as the next call's hint. */
  index: number
  /** Closest point on the centreline. */
  px: number
  py: number
  pz: number
  /** Signed planar offset from the centreline: positive to the left of travel. */
  lateral: number
  /** Planar distance from the centreline, always positive. */
  distance: number
}

export function createPathQuery(): PathQuery {
  return { s: 0, index: 0, px: 0, py: 0, pz: 0, lateral: 0, distance: 0 }
}

export class Path {
  /** Sample positions and cumulative 3-D arc length. Parallel arrays, never reallocated. */
  readonly xs: Float64Array
  readonly ys: Float64Array
  readonly zs: Float64Array
  readonly ss: Float64Array

  /** Total length, metres. */
  readonly length: number

  constructor(
    controlPoints: readonly (readonly [number, number, number])[],
    samplesPerSegment = 16,
  ) {
    if (controlPoints.length < 2) {
      throw new Error(`Path: needs at least 2 control points, got ${controlPoints.length}`)
    }
    if (samplesPerSegment < 1) throw new Error('Path: samplesPerSegment must be at least 1')

    const n = controlPoints.length
    const spans = n - 1
    const count = spans * samplesPerSegment + 1

    const xs = new Float64Array(count)
    const ys = new Float64Array(count)
    const zs = new Float64Array(count)
    const ss = new Float64Array(count)

    // Endpoints are duplicated so the curve starts and ends exactly on the author's points.
    const at = (i: number): readonly [number, number, number] =>
      controlPoints[i < 0 ? 0 : i >= n ? n - 1 : i]!

    let k = 0
    for (let span = 0; span < spans; span++) {
      const p0 = at(span - 1)
      const p1 = at(span)
      const p2 = at(span + 1)
      const p3 = at(span + 2)
      for (let j = 0; j < samplesPerSegment; j++) {
        const t = j / samplesPerSegment
        xs[k] = catmullRom(p0[0], p1[0], p2[0], p3[0], t)
        ys[k] = catmullRom(p0[1], p1[1], p2[1], p3[1], t)
        zs[k] = catmullRom(p0[2], p1[2], p2[2], p3[2], t)
        k++
      }
    }
    const last = controlPoints[n - 1]!
    xs[k] = last[0]
    ys[k] = last[1]
    zs[k] = last[2]

    ss[0] = 0
    for (let i = 1; i < count; i++) {
      const dx = xs[i]! - xs[i - 1]!
      const dy = ys[i]! - ys[i - 1]!
      const dz = zs[i]! - zs[i - 1]!
      ss[i] = ss[i - 1]! + Math.sqrt(dx * dx + dy * dy + dz * dz)
    }

    this.xs = xs
    this.ys = ys
    this.zs = zs
    this.ss = ss
    this.length = ss[count - 1]!
    if (this.length <= 0) throw new Error('Path: control points describe no length')
  }

  get sampleCount(): number {
    return this.xs.length
  }

  /** Sample index at or before arc length `s`. */
  indexAt(s: number): number {
    const ss = this.ss
    let lo = 0
    let hi = ss.length - 1
    if (s <= 0) return 0
    if (s >= this.length) return hi - 1
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1
      if (ss[mid]! <= s) lo = mid
      else hi = mid
    }
    return lo
  }

  /** Position at arc length `s`, clamped to the ends. */
  pointAt(s: number, out: { x: number; y: number; z: number }): void {
    const i = this.indexAt(s)
    const s0 = this.ss[i]!
    const s1 = this.ss[i + 1]!
    const span = s1 - s0
    const t = span > 0 ? (s - s0) / span : 0
    out.x = this.xs[i]! + (this.xs[i + 1]! - this.xs[i]!) * t
    out.y = this.ys[i]! + (this.ys[i + 1]! - this.ys[i]!) * t
    out.z = this.zs[i]! + (this.zs[i + 1]! - this.zs[i]!) * t
  }

  /** Unit tangent in three dimensions at arc length `s`. */
  tangentAt(s: number, out: { x: number; y: number; z: number }): void {
    const i = this.indexAt(s)
    const dx = this.xs[i + 1]! - this.xs[i]!
    const dy = this.ys[i + 1]! - this.ys[i]!
    const dz = this.zs[i + 1]! - this.zs[i]!
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz)
    if (len === 0) {
      out.x = 1
      out.y = 0
      out.z = 0
      return
    }
    const inv = 1 / len
    out.x = dx * inv
    out.y = dy * inv
    out.z = dz * inv
  }

  /**
   * Rise per unit of **planar** travel at arc length `s`.
   *
   * This is the number that decides whether a ball makes a ramp. It is the gradient a ball
   * rolling across the playfield actually climbs, which is not the same as the 3-D tangent's
   * z component — a near-vertical section covers almost no ground per metre of wire.
   */
  planarSlopeAt(s: number): number {
    const i = this.indexAt(s)
    const dx = this.xs[i + 1]! - this.xs[i]!
    const dy = this.ys[i + 1]! - this.ys[i]!
    const dz = this.zs[i + 1]! - this.zs[i]!
    const planar = Math.sqrt(dx * dx + dy * dy)
    return planar > 1e-12 ? dz / planar : 0
  }

  /** Closest point by planar distance, searching the whole path. */
  project(x: number, y: number, out: PathQuery): void {
    this.projectRange(x, y, 0, this.xs.length - 2, out)
  }

  /**
   * Closest point, searching only near a previous answer.
   *
   * A ball moves at most 8 mm in a step against samples a few millimetres apart, so a
   * window of four either side is generous. Falling back to a full scan on every step would
   * turn a ramp into the most expensive object on the playfield.
   */
  projectNear(x: number, y: number, hint: number, reach: number, out: PathQuery): void {
    const last = this.xs.length - 2
    let from = hint - reach
    let to = hint + reach
    if (from < 0) from = 0
    if (to > last) to = last
    this.projectRange(x, y, from, to, out)
  }

  private projectRange(x: number, y: number, from: number, to: number, out: PathQuery): void {
    let bestD2 = Infinity
    let bestI = from
    let bestT = 0

    for (let i = from; i <= to; i++) {
      const ax = this.xs[i]!
      const ay = this.ys[i]!
      const ex = this.xs[i + 1]! - ax
      const ey = this.ys[i + 1]! - ay
      const len2 = ex * ex + ey * ey
      let t = len2 > 0 ? ((x - ax) * ex + (y - ay) * ey) / len2 : 0
      if (t < 0) t = 0
      else if (t > 1) t = 1
      const dx = x - (ax + ex * t)
      const dy = y - (ay + ey * t)
      const d2 = dx * dx + dy * dy
      if (d2 < bestD2) {
        bestD2 = d2
        bestI = i
        bestT = t
      }
    }

    const ax = this.xs[bestI]!
    const ay = this.ys[bestI]!
    const az = this.zs[bestI]!
    const ex = this.xs[bestI + 1]! - ax
    const ey = this.ys[bestI + 1]! - ay
    const ez = this.zs[bestI + 1]! - az

    out.index = bestI
    out.px = ax + ex * bestT
    out.py = ay + ey * bestT
    out.pz = az + ez * bestT
    out.s = this.ss[bestI]! + (this.ss[bestI + 1]! - this.ss[bestI]!) * bestT

    const planar = Math.sqrt(ex * ex + ey * ey)
    const dx = x - out.px
    const dy = y - out.py
    out.distance = Math.sqrt(dx * dx + dy * dy)
    // Left of travel is positive: the planar left-hand perpendicular of the tangent.
    out.lateral = planar > 1e-12 ? (dx * -ey + dy * ex) / planar : 0
  }

  /**
   * Planar points offset laterally from the centreline, for generating ramp walls.
   *
   * Not used at runtime; called once when a ramp builds its geometry.
   */
  offsetPolyline(offset: number, stride = 1): [number, number][] {
    const out: [number, number][] = []
    const count = this.xs.length
    for (let i = 0; i < count; i += stride) {
      const j = i === count - 1 ? i - 1 : i
      const ex = this.xs[j + 1]! - this.xs[j]!
      const ey = this.ys[j + 1]! - this.ys[j]!
      const len = Math.sqrt(ex * ex + ey * ey)
      if (len < 1e-12) continue
      out.push([this.xs[i]! + (-ey / len) * offset, this.ys[i]! + (ex / len) * offset])
    }
    // Always finish exactly on the end cap, whatever the stride left behind.
    const n = count - 1
    const ex = this.xs[n]! - this.xs[n - 1]!
    const ey = this.ys[n]! - this.ys[n - 1]!
    const len = Math.sqrt(ex * ex + ey * ey)
    if (len > 1e-12) {
      const lastX = this.xs[n]! + (-ey / len) * offset
      const lastY = this.ys[n]! + (ex / len) * offset
      const tail = out[out.length - 1]
      if (!tail || tail[0] !== lastX || tail[1] !== lastY) out.push([lastX, lastY])
    }
    return out
  }
}

/** Uniform Catmull-Rom, the standard form. Polynomial only — no transcendentals. */
function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t
  const t3 = t2 * t
  return (
    0.5 *
    (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
  )
}
